import * as React from 'react'
import { RouteComponentProps } from 'react-router'
import {
  IActiveTripResponseV2,
  ICoordinates,
  ITripFilters,
  IDriverSearchObject,
  IVehicleFilters,
  IVehicleObj,
  ISimpleDriverDTO,
  IPagination,
  ITripEdit,
  IDriverScheduleDTO
} from '../../../types'
import ErrorBoundary from '../../ErrorBoundary'
import TripsSchedulerCalendarv1 from './TripsSchedulerCalendarv1'
import moment from 'moment'

export interface ITripsPaneProps extends RouteComponentProps {
  origin: ICoordinates | null
  destination: ICoordinates | null
  polylines: string | null
  currentUserId: string | undefined
  currentOrganizationId: string
  showMembers: boolean
  visible: boolean
  drivers: IDriverSearchObject[]
  liveDrivers: IDriverSearchObject[]
  vehicles: IVehicleObj[]
  vehicleFilters: IVehicleFilters
  trips: IActiveTripResponseV2[]
  tripFilters: ITripFilters
  operatorSchedule: IDriverScheduleDTO[]
  getOrgOperators: (orgId: string) => Promise<any>
  getOperators: (organizationId: string, serviceIds?: string[] | undefined) => Promise<any>
  getOperatorMetrics: (organizationId: string) => Promise<any>
  fetchAllVehicles: (params: IVehicleFilters) => void
  updateVehicleFilters: (params: IVehicleFilters) => void
  fetchTrips: (params: ITripFilters, pagination?: IPagination) => Promise<any>
  assignOperator: (tripId: string, lastStatus: string, operator: ISimpleDriverDTO, isOndemandTrip: boolean) => void
  updateTripFilters: (filters: ITripFilters) => void
  setCurrentTrip: (tripId: string) => void
  assignScheduledGroupRideOperator: (id: string, operator: ISimpleDriverDTO) => Promise<any>
  unAssignScheduledGroupRideOperator: (id: string) => Promise<any>
  fetchOperatorSchedule: () => void
  saveData: (params: ITripEdit) => Promise<any>
  updateSingleTrip: (trip: IActiveTripResponseV2) => Promise<any>
}

interface ITripsDashboardState {
  pageIndex: number
  pages: number
}

const PAGE_SIZE = 100
const INTERVAL = 20000

class TripsScheduler extends React.Component<ITripsPaneProps, ITripsDashboardState> {
  public state = {
    pageIndex: 0,
    pages: 1,
    timeStartFilter: '',
    timeEndFilter: ''
  }

  constructor(props: any) {
    super(props)

    this.state.timeStartFilter = moment()
      .startOf('day')
      .toISOString()
    this.state.timeEndFilter = moment()
      .endOf('day')
      .toISOString()
  }

  public componentDidMount() {
    const {
      currentOrganizationId,
      getOperators,
      getOrgOperators,
      fetchAllVehicles,
      updateVehicleFilters,
      updateTripFilters,
      tripFilters
    } = this.props

    const { serviceIds = [] } = tripFilters

    if (currentOrganizationId) {
      const vehicleInitialFilter = {
        organizationId: currentOrganizationId,
        type: 'active',
        page: 0,
        take: 1000,
        recordsPerPage: 1000
      }
      updateVehicleFilters(vehicleInitialFilter)
      fetchAllVehicles(vehicleInitialFilter)
      getOrgOperators(currentOrganizationId)
      getOperators(currentOrganizationId, serviceIds)
      const pagination = { skip: 0, take: PAGE_SIZE }

      updateTripFilters({
        ...this.props.tripFilters,
        ...pagination,
        timeStart: this.state.timeStartFilter,
        timeEnd: this.state.timeEndFilter,
        sortBy: !tripFilters.sortBy ? ['lastStatus-desc'] : tripFilters.sortBy,
        getDashBoardData: true,
        timeField: 'createdAndPickup'
      })
      this.getData()
    }
  }

  public async componentDidUpdate(prevProps: ITripsPaneProps) {
    const { tripFilters, getOrgOperators } = this.props
    const { serviceIds = [] } = tripFilters
    if (JSON.stringify(prevProps.tripFilters) !== JSON.stringify(tripFilters)) {
      this.getData()
      this.getDriversList(serviceIds)
    }
    if (prevProps.currentOrganizationId !== this.props.currentOrganizationId) {
      if (this.props.currentOrganizationId) {
        getOrgOperators(this.props.currentOrganizationId)
      }
    }
  }
  public componentWillUnmount(): void {
    const newFilter: ITripFilters = {
      productIds: undefined,
      serviceIds: undefined,
      serviceId: undefined,
      status: undefined,
      statusGroup: 'active',
      timeField: 'tripCreated',
      timeStart: undefined,
      timeEnd: undefined,
      sortBy: ['lastStatus-desc'],
      skip: undefined,
      take: undefined,
      getDashBoardData: undefined
    }
    this.props.updateTripFilters(newFilter)
  }

  public render() {
    const {
      currentOrganizationId,
      vehicles,
      vehicleFilters,
      drivers,
      trips,
      operatorSchedule,
      fetchTrips,
      fetchAllVehicles,
      assignOperator,
      tripFilters,
      updateTripFilters,
      assignScheduledGroupRideOperator,
      unAssignScheduledGroupRideOperator,
      setCurrentTrip,
      fetchOperatorSchedule,
      liveDrivers,
      updateSingleTrip
    } = this.props
    return (
      <ErrorBoundary>
        <div className='ml1 mr1' style={{ height: '99%', borderStyle: 'groove', marginTop: '1px' }}>
          <TripsSchedulerCalendarv1
            orgId={currentOrganizationId}
            drivers={drivers}
            activeDrivers={liveDrivers}
            vehicles={vehicles}
            vehicleFilters={vehicleFilters}
            trips={trips}
            operatorSchedule={operatorSchedule}
            fetchTrips={fetchTrips}
            fetchAllVehicles={fetchAllVehicles}
            assignOperator={assignOperator}
            tripFilters={tripFilters}
            updateTripFilters={updateTripFilters}
            setCurrentTrip={setCurrentTrip}
            assignScheduledGroupRideOperator={assignScheduledGroupRideOperator}
            unAssignScheduledGroupRideOperator={unAssignScheduledGroupRideOperator}
            fetchOperatorSchedule={fetchOperatorSchedule}
            saveData={this.props.saveData}
            updateSingleTrip={updateSingleTrip}
          />
        </div>
      </ErrorBoundary>
    )
  }

  private getData = async () => {
    const { tripFilters } = this.props

    const formattedFilters = {
      ...tripFilters,
      ...(!tripFilters.timeStart || !tripFilters.timeEnd
        ? {
            timeStart: moment()
              .startOf('day')
              .toISOString(),
            timeEnd: moment()
              .endOf('day')
              .toISOString()
          }
        : {}),
      timeField: 'createdAndPickup'
    }

    const pagination = { skip: this.state.pageIndex * PAGE_SIZE, take: PAGE_SIZE }
    const total = await this.props.fetchTrips(formattedFilters, pagination)
    this.setState({
      pages: Math.ceil(total / PAGE_SIZE)
    })
  }

  private getDriversList = async (serviceIds: string[]) => {
    const { currentOrganizationId, getOperators } = this.props
    await getOperators(currentOrganizationId, serviceIds)
  }
}

export default TripsScheduler



// WEBPACK FOOTER //
// ./src/components/trips/timeline-view/TripsScheduler.tsx